import { cn } from '@/lib/utils/cn';
import type { MetricTone } from '../MetricCard';

const toneStroke: Record<MetricTone, string> = {
  default: '#2563eb',
  success: '#10b981',
  warning: '#f59e0b',
  danger:  '#ef4444',
  shield:  '#10b981',
  detect:  '#f97316',
  trust:   '#06b6d4',
  signal:  '#8b5cf6',
};

const toneFill: Record<MetricTone, string> = {
  default: 'rgba(37,99,235,0.12)',
  success: 'rgba(16,185,129,0.12)',
  warning: 'rgba(245,158,11,0.12)',
  danger:  'rgba(239,68,68,0.12)',
  shield:  'rgba(16,185,129,0.12)',
  detect:  'rgba(249,115,22,0.12)',
  trust:   'rgba(6,182,212,0.12)',
  signal:  'rgba(139,92,246,0.12)',
};

interface SparklineProps {
  values: number[];
  tone?: MetricTone;
  className?: string;
  height?: number;
}

export function Sparkline({ values, tone = 'default', className, height = 40 }: SparklineProps) {
  if (!values || values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 100;
  const h = height;
  const pad = 2;

  const xs = values.map((_, i) => pad + (i / (values.length - 1)) * (w - pad * 2));
  const ys = values.map((v) => h - pad - ((v - min) / range) * (h - pad * 2));

  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${(ys[i] ?? 0).toFixed(1)}`).join(' ');
  const lastX = (xs[xs.length - 1] ?? pad).toFixed(1);
  const firstX = (xs[0] ?? pad).toFixed(1);
  const areaPath = `${linePath} L${lastX},${(h - pad).toFixed(1)} L${firstX},${(h - pad).toFixed(1)} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={cn('w-full', className)}
      aria-hidden
    >
      <path d={areaPath} fill={toneFill[tone]} />
      <path d={linePath} fill="none" stroke={toneStroke[tone]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
